import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { CheckCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscriptionSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("loading");
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    // Get token from localStorage directly since the app may have just reloaded from Stripe redirect
    const storedToken = localStorage.getItem("ventaphy_token");
    
    if (!sessionId) {
      navigate("/pricing");
      return;
    }
    
    if (!storedToken) {
      // User is not logged in - redirect to auth with return URL
      toast.error("Please log in to complete your subscription");
      navigate("/auth", { state: { returnTo: `/subscription/success?session_id=${sessionId}` } });
      return;
    }

    const pollStatus = async () => {
      try {
        const response = await axios.get(
          `${API}/subscription/status/${sessionId}`,
          { headers: { Authorization: `Bearer ${storedToken}` } }
        );

        if (response.data.payment_status === "paid") {
          setStatus("success");
          await refreshUser();
        } else if (response.data.status === "expired") {
          setStatus("failed");
        } else if (attempts < 10) {
          setTimeout(() => setAttempts((a) => a + 1), 2000);
        } else {
          setStatus("timeout");
        }
      } catch (error) {
        console.error("Status check error:", error);
        if (attempts < 10) {
          setTimeout(() => setAttempts((a) => a + 1), 2000);
        } else {
          setStatus("failed");
        }
      }
    };

    pollStatus();
  }, [sessionId, attempts, navigate, refreshUser]);

  if (status === "loading") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
          <h2 className="font-serif text-xl font-bold mb-2">
            Confirming your subscription...
          </h2>
          <p className="text-muted-foreground">
            This will only take a moment
          </p>
        </motion.div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.2 }}
            className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="w-10 h-10 text-primary" />
          </motion.div>
          
          <h1 className="font-serif text-2xl font-bold mb-2">
            Welcome to Ventaphy
          </h1>
          <p className="text-muted-foreground mb-8">
            Your subscription is now active. You&apos;re all set to vent.
          </p>
          
          <Button
            onClick={() => navigate("/persona")}
            className="h-14 px-8 rounded-full text-lg bg-primary hover:bg-primary/90"
            data-testid="start-venting-btn"
          >
            Start Venting
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center"
      >
        <h1 className="font-serif text-2xl font-bold mb-2">
          {status === "timeout" ? "Taking longer than expected" : "Payment Failed"}
        </h1>
        <p className="text-muted-foreground mb-8">
          {status === "timeout" 
            ? "Your payment may still be processing. Check your email for confirmation."
            : "Something went wrong with your payment. Please try again."}
        </p>
        
        <div className="space-y-3">
          <Button
            onClick={() => navigate("/pricing")}
            className="h-12 px-8 rounded-full bg-primary hover:bg-primary/90"
            data-testid="try-again-btn"
          >
            Try Again
          </Button>
          <br />
          <Button
            onClick={() => navigate("/persona")}
            variant="ghost"
            className="text-muted-foreground"
            data-testid="continue-free-btn"
          >
            Continue with Free Plan
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
